import {
  Component,
  OnInit, ViewChildren
} from '@angular/core';
import {
  NetscopeApiService,
  Success,
  Failure,
  Response
} from "@app/services/netscope-api.service";
import {
  Router
} from "@angular/router";
import {TranslocoService} from "@ngneat/transloco";
import {NetscopeUiService} from "@app/services/netscope-ui.service";
import {SelectionComboboxComponent} from "@app/selection-combobox/selection-combobox.component";
import { languages } from '@codemirror/language-data';
import {CompletionContext} from "@codemirror/autocomplete";
import { sql, StandardSQL } from '@codemirror/lang-sql';

class Criterion {
    basic: boolean = false;
}

export class CustomProtocolsRules {
  showEditingFeatures: boolean;
  protocol: string;
  checkMade: boolean;
  nameIsValid: boolean;
  criterion: Criterion;
  criterion_v2: string;
  version: "v1" | "v2" = "v1";
}

@Component({
  standalone: false,
  selector: 'app-netscope-custom-protocols',
  templateUrl: './netscope-custom-protocols.component.html',
  styleUrls: ['./netscope-custom-protocols.component.css']
})
export class NetscopeCustomProtocolsComponent implements OnInit {

  isLoading = false;
  failureMode = false;
  isPushingRules = false;
  reloadButtonColorClass = "btn-primary";
  customProtocolsData;
  rules: CustomProtocolsRules[] = []
  editingRules = [];

  selectedFile: Criterion = new Criterion();

  computingResources = [];

  @ViewChildren("combobox")
  comboboxes: SelectionComboboxComponent;

  arithmetic_operators = [">", "<", "=", ">=", "<="];
  unary_operators = ["not"];
  n_ary_operators = ["or", "and"];
  targets = ["src.uuid", "src.ip", "dst.uuid", "dst.ip", "port"];
  numberTargets = ["port"];
  vmTargets = ["src.uuid", "dst.uuid"];
  ipTargets = ["src.ip", "dst.ip"];
  labels = {
    "and": "all the following are true",
    "or": "one at least is true",
    "not": "the following is false",
    "src.uuid": "source",
    "src.ip": "source ip",
    "dst.uuid": "destination",
    "dst.ip": "destination ip",
    "port": "port"
  };

  codeEditorSetup = {}
  languages = languages;

  extensions = [
    sql({
      dialect: StandardSQL,
      schema: {
        flows: [
          'start_time',
          'src_name', 'src_resource_uuid', 'src_ipaddr',
          'dst_name', 'dst_resource_uuid', 'dst_ipaddr',
          'dst_port', 'protocol', 'direction', 'proto_raw',
          'exchanged_bytes', 'exchanged_packets'
        ],
      },
      // optional: complete `users` columns at the top level without a table prefix
      defaultTable: 'flows',
    }),
  ];

  replaceDoubleQuotesInterval;

  constructor(private netscopeService: NetscopeApiService, private route: Router,
              public translocoService: TranslocoService, private netscopeUiService: NetscopeUiService) {
  }

  ngOnInit(): void {
    this.netscopeUiService.setViewLabel("custom_protocols.name");
    this.netscopeUiService.setIconShape("tags");

    this.replaceDoubleQuotesInterval = setInterval(() => {
      for (let rule of this.rules) {
        if (rule.version != "v2" || !rule.showEditingFeatures) {
          continue;
        }

        if (rule.criterion_v2 && rule.criterion_v2.includes('"')) {
          rule.criterion_v2 = rule.criterion_v2.replaceAll('"', "'");
        }
      }
    }, 1000);

    this.reloadData();
  }

  ngOnDestroy = () => {
    clearInterval(this.replaceDoubleQuotesInterval);
  }

  myCompletions = (context: CompletionContext) => {
    let word = context.matchBefore(/\w*/)
    if (word.from == word.to && !context.explicit)
      return null
    return {
      from: word.from,
      options: [
        {label: "match", type: "keyword"},
        {label: "hello", type: "variable", info: "(World)"},
        {label: "magic", type: "text", apply: "⠁⭒*.✩.*⭒⠁", detail: "macro"}
      ]
    }
  }

  reloadData = () => {
    this.isLoading = true;
    this.failureMode = false;

    this.netscopeService.getLastInfrastructureVersion().subscribe((infrastructureVersionResponse: Response<any>) => {
      let infrastructureVersion;

      if (infrastructureVersionResponse instanceof Success) {
        infrastructureVersion = infrastructureVersionResponse.element;
      } else {
        this.failureMode = true;
        this.isLoading = false;
        return;
      }

      infrastructureVersion.topology.vm_only_topology.vms.map((vm) => {
        this.computingResources.push({
          "name": vm.name,
          "uuid": vm.uuid.split(":")[1],
          "full_uuid": vm.uuid,
          "type": "vm"
        });
      });
      infrastructureVersion.topology.vm_only_topology.hosts.map((server) => {
        this.computingResources.push({
          "name": server.name,
          "uuid": server.uuid.split(":")[1],
          "full_uuid": server.uuid,
          "type": "server"
        });
      });

      this.netscopeService.getCustomProtocolsRules().subscribe((customProtocolsDataResponse: Response<any>) => {

        if (customProtocolsDataResponse instanceof Failure) {
          this.failureMode = true;
          this.isLoading = false;
          return;
        }

        let customProtocolsData;
        if (customProtocolsDataResponse instanceof Success) {
          customProtocolsData = customProtocolsDataResponse.element;
        }

        this.customProtocolsData = customProtocolsData;
        this.isLoading = false;
        this.reloadRules()
      });
    });

  }

  reloadRules = () => {
    this.rules = this.processRulesFromApiToAngular(this.customProtocolsData.custom_rules);
  }

  processCriterion = (protocolRule, criterion, parent, vmsUuidsIdx, serversUuidsIdx) => {

    if (criterion == undefined) {
      return undefined;
    }

    let result = {
      name: "no criterion",
      files: [],
      type: 'empty',
      operator: "no_operator",
      editing: false,
      basic: false,
      showEditingButton: false,
      logicalModel: undefined,
      logicalModel2: criterion,
      parent: parent,
      protocolRule: protocolRule,
      errors: []
    };

    if (typeof (criterion) === "object") {
      Object.entries(criterion).map((e) => {
        let operator = e[0];
        if (this.n_ary_operators.indexOf(operator) !== -1) {
          // @ts-ignore
          let subcriterion = e[1].map((subcriteria) => this.processCriterion(protocolRule, subcriteria, result, vmsUuidsIdx, serversUuidsIdx))
          let flattenSubcriterion = subcriterion.flat();

          Object.assign(result, {
            name: this.labels[operator],
            files: flattenSubcriterion,
            type: 'nAryOperator',
            operator: operator,
            logicalModel: e,
          });
        }
        if (this.unary_operators.indexOf(operator) !== -1) {
          // @ts-ignore
          let subcriterion = e[1].map((subcriteria) => this.processCriterion(protocolRule, subcriteria, result, vmsUuidsIdx, serversUuidsIdx))
          let flattenSubcriterion = subcriterion.flat();

          Object.assign(result, {
            name: this.labels[operator],
            files: flattenSubcriterion,
            type: 'unaryOperator',
            operator: operator,
            logicalModel: e,
          });
        }
        if (this.arithmetic_operators.indexOf(operator) !== -1) {
          let parts = Object.entries(e[1]);
          let operatorStr = e[0];
          let leftValue = parts[0][0];
          let rightValue = this.numberTargets.indexOf(leftValue) === -1 ? parts[0][1] : parseInt(
            parts[0][1]);
          let leftLabel = this.labels[leftValue];
          let rightLabel = rightValue;
          let rightObject = undefined

          // Check if rightLabel is a host or a VM
          if (vmsUuidsIdx.hasOwnProperty(rightLabel)) {
            rightObject = {
              uuid: rightLabel,
              name: vmsUuidsIdx[rightLabel],
              type: "vm"
            };
          }

          if (serversUuidsIdx.hasOwnProperty(rightLabel)) {
            rightObject = {
              uuid: rightLabel,
              name: serversUuidsIdx[rightLabel],
              type: "server"
            };
          }

          let name = `${leftLabel} ${operatorStr} ${rightValue}`;

          Object.assign(result, {
            name: name,
            type: 'arithmeticOperator',
            left: leftValue,
            right: rightValue,
            leftLabel: leftLabel,
            rightLabel: rightLabel,
            rightObject: rightObject,
            operator: operatorStr,
            logicalModel: e,
            logicalModel2: criterion,
            parent: parent
          });
        }
      });
    }

    return result;
  }

  processRulesFromApiToAngular = (rules) => {
    // Collect uuids of VMs and ESXi servers
    let vmsUuidsIdx = {};

    this.computingResources.filter(resource => resource.type === "vm").map((vm) => {
      vmsUuidsIdx[vm.uuid] = vm.name;
      // TODO: check for XenOrchestra support
      vmsUuidsIdx[`vim.VirtualMachine:${vm.uuid}`] = vm.name;
    })
    let serversUuidsIdx = {};
    this.computingResources.filter(resource => resource.type === "server").map((server) => {
      serversUuidsIdx[server.uuid] = server.name;
      // TODO: check for XenOrchestra support
      serversUuidsIdx[`vim.HostSystem:${server.uuid}`] = server.name;
    })

    return rules.map((protocolRule) => {
      let processedCriterion = this.processCriterion(protocolRule, protocolRule.criterion, undefined,
        vmsUuidsIdx, serversUuidsIdx);
      console.log(processedCriterion);
      return {
        rawRule: protocolRule,
        protocol: protocolRule.protocol,
        showEditingFeatures: this.editingRules.indexOf(protocolRule) !== -1,
        criterion: processedCriterion,
        criterion_v2: protocolRule.criterion_v2,
        checkMade: false,
        nameIsValid: true,
        version: protocolRule.hasOwnProperty("criterion") ? "v1": "v2"
      };
    });
  }

  getChildren = (folder) => folder.files;

  pushData = () => {
    this.isPushingRules = true;
    this.netscopeService.pushCustomProtocols(this.customProtocolsData).subscribe((response:
                                                                                    Response<object>) => {
      setTimeout(() => {
        this.isPushingRules = false;
      }, 300);
    })
  }

  processEditing = (file) => {
    file.editing = false;

    if (file.type === "nAryOperator") {
      let existingKey = Object.keys(file.logicalModel2)[0];
      if (file.operator !== existingKey) {
        file.logicalModel2[file.operator] = file.logicalModel2[existingKey];
        delete file.logicalModel2[existingKey];
      }
    }

    if (file.type === "unaryOperator") {
      let existingKey = Object.keys(file.logicalModel2)[0];
      if (file.operator !== existingKey) {
        file.logicalModel2[file.operator] = file.logicalModel2[existingKey];
        delete file.logicalModel2[existingKey];
      }
    }

    if (file.type === "arithmeticOperator") {
      let existingKey = Object.keys(file.logicalModel2)[0];
      if (file.operator !== existingKey) {
        file.logicalModel2[file.operator] = file.logicalModel2[existingKey];
        delete file.logicalModel2[existingKey];
      }
      let existingLeft = Object.keys(file.logicalModel2[file.operator])[0];
      if (file.left !== existingLeft) {
        delete file.logicalModel2[file.operator][existingLeft];
      }

      if (this.numberTargets.indexOf(file.left) !== -1) {
        file.right = parseInt(file.right);
      }

      file.logicalModel2[file.operator][file.left] = file.right;
    }

    this.reloadRules();
  }

  deleteRule = (file) => {
    if (file.parent === undefined) {
      file.protocolRule.criterion = undefined;
    } else {
      let existingKey = Object.keys(file.parent.logicalModel2)[0];
      file.parent.logicalModel2[existingKey] = file.parent.logicalModel2[existingKey].filter((f) => f !== file
        .logicalModel2);
    }
    this.reloadRules();
  }

  updateProtocolName = (protocolRule) => {
    protocolRule.rawRule.protocol = protocolRule.protocol;
  }

  deleteProtocol = (protocol) => {
    let indexOfProtocol = this.customProtocolsData.custom_rules.indexOf(protocol.rawRule);
    if (indexOfProtocol !== -1) {
      this.customProtocolsData.custom_rules = this.customProtocolsData.custom_rules.filter((e) => e !==
        protocol.rawRule);
      this.reloadRules();
    }
  }

  startEditing = (rule) => {
    rule.showEditingFeatures = true;
    this.editingRules.push(rule.rawRule);
  }

  stopEditing = (rule) => {
    let checkResult = this.checkRule(rule);
    console.log(`CheckResult => ${checkResult}`);

    if (checkResult) {
      rule.showEditingFeatures = false;
      this.editingRules = this.editingRules.filter((r) => r !== rule.rawRule);

      if (rule.version == 'v2') {
        rule.rawRule.criterion_v2 = rule.criterion_v2;
      }

      this.reloadRules();
    }
  }

  addCompositeCriteria = (protocolRule, file) => {
    let newRule = {
      and: []
    };
    if (file === undefined) {
      protocolRule.rawRule.criterion = newRule
    } else {
      let existingKey = Object.keys(file.logicalModel2)[0];
      file.logicalModel2[existingKey].push(newRule);
    }
    this.reloadRules();
  }

  addNotCriteria = (protocolRule, file) => {
    let newRule = {
      not: []
    };
    if (file === undefined) {
      protocolRule.rawRule.criterion = newRule
    } else {
      let existingKey = Object.keys(file.logicalModel2)[0];
      file.logicalModel2[existingKey].push(newRule);
    }
    this.reloadRules();
  }

  addSimpleCriteria = (protocolRule, file) => {
    let newRule = {
      "=": {
        "src.uuid": ""
      },
    };
    if (file === undefined) {
      protocolRule.rawRule.criterion = newRule
    } else {
      let existingKey = Object.keys(file.logicalModel2)[0];
      file.logicalModel2[existingKey].push(newRule);
    }
    this.reloadRules();
  }

  updateRightSelectedResource = (file, computingResource) => {
    file.right = computingResource.full_uuid;
  }

  keyboardSelect = (file, event) => {
    file.right = event.model.full_uuid;
  }

  addNewProtocol = () => {
    let newProtocolName = "new_protocol";
    this.customProtocolsData.custom_rules.push({
      protocol: newProtocolName,
      criterion: undefined
    })
    this.reloadRules();
  }

  checkRule = (rule) => {
    rule.checkMade = true;
    console.log(`checkRule(${rule})`);

    if (rule.version == "v2") {
      return this.checkProtocolName(rule);
    }

    return this.checkProtocolName(rule) && this.checkCriterion(rule.criterion);
  }

  checkProtocolName = (rule) => {
    const nameRegex = /^[A-Za-z0-9_]+$/;
    let checkNameResult = nameRegex.test(rule.protocol);

    rule.nameIsValid = checkNameResult;

    return checkNameResult;
  }

  checkCriterion = (criterion) => {
    let vmUuids = this.computingResources.filter((res) => res.type === "vm").map((res) => res.full_uuid);

    console.log(`checkCriterion(${criterion})`);

    if (criterion.type === "nAryOperator") {
      let isOk = true;
      for (let subCriterion of criterion.files) {
        if (!this.checkCriterion(subCriterion)) {
          isOk = false;
        }
      }
      if (!isOk) {
        criterion.errors = [`at least one of the sub criterion is not valid`]
      }
      return isOk;
    }

    if (criterion.type === "unaryOperator") {
      let isOk = true;
      for (let subCriterion of criterion.files) {
        if (!this.checkCriterion(subCriterion)) {
          isOk = false;
        }
      }
      if (!isOk) {
        criterion.errors = [`at least one of the sub criterion is not valid`]
      }
      return isOk;
    }

    if (criterion.type === "arithmeticOperator") {
      if (this.ipTargets.indexOf(criterion.left) !== -1) {
        /* Check if string is IP */
        function checkIfValidIP(str) {
          // Regular expression to check if string is a IP address
          const regexExp =
            /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/gi;

          return regexExp.test(str);
        }

        let isValidIp = checkIfValidIP(criterion.right);
        if (!isValidIp) {
          criterion.errors = [`${criterion.right} is not a valid IP`]
        }
        return isValidIp;
      }
      if (this.numberTargets.indexOf(criterion.left) !== -1) {
        let isNumber = false
        try {
          parseInt(criterion.right);
          isNumber = !isNaN(criterion.right);
        } catch (e) {
        }
        if (!isNumber) {
          criterion.errors = [`${criterion.right} is not a valid number`]
        }
        return isNumber;
      }
      if (this.vmTargets.indexOf(criterion.left) !== -1) {
        let isValidVmUuid = true;
        if (vmUuids.indexOf(criterion.right) === -1) {
          isValidVmUuid = false;
        }
        if (!isValidVmUuid) {
          criterion.errors = [`${criterion.right} is not a known VM uuid`]
        }
        return isValidVmUuid;
      }
    }

    return true;
  }

  setLanguage = (language: "french" | "english") => {
    let languageMap: Map<string, string> = new Map([
      ["french", "fr"],
      ["english", "en"]
    ]);
    let language_code = languageMap.get(language);
    this.translocoService.setDefaultLang(language_code);
    this.translocoService.setActiveLang(language_code);
    localStorage.setItem("language", language_code);
  }
}

